import type { Tab, TemplateSection } from 'types';

export const DEFAULT_TAB_KEY = 'default';

/**
 * Id used as a DOM id and a dnd-kit sortable id.
 * A JSON tuple keeps a missing tab distinct from any real tab id (including "default"),
 * and percent-encoding keeps separators and whitespace inside user-controlled values from aliasing another section.
 */
export const getSectionKey = (name: string, tabId?: string | null): string => encodeURIComponent(JSON.stringify([tabId ?? null, name]));

export const findTemplateSectionIndex = (sections: TemplateSection[], name: string, tabId?: string | null): number =>
  sections.findIndex((section) => {
    if (section.name !== name) return false;

    if (tabId === undefined) return true;

    return (section.tabId ?? null) === (tabId ?? null);
  });

export const isSectionNameUnique = (sections: TemplateSection[], name: string, tabId: string | null): boolean =>
  !sections.some((section) => (section.tabId ?? null) === (tabId ?? null) && section.name === name);

export const makeUniqueSectionName = (name: string, existingNames: Set<string>): string => {
  if (!existingNames.has(name)) return name;

  let suffix = 1;
  let candidate = `${name}_${suffix}`;

  while (existingNames.has(candidate)) {
    suffix += 1;
    candidate = `${name}_${suffix}`;
  }

  return candidate;
};

export const uniquifySectionNameOnTab = (section: TemplateSection, existingNames: Set<string>): void => {
  const uniqueName = makeUniqueSectionName(section.name, existingNames);

  if (uniqueName !== section.name) {
    const previousName = section.name;

    section.name = uniqueName;
    section.title = (section.title ?? []).map((translation) =>
      translation.value === previousName ? { ...translation, value: uniqueName } : translation
    );
  }

  existingNames.add(section.name);
};

export const getTabNameCandidates = (tab: Pick<Tab, 'title' | 'titleTranslations'>): string[] => {
  const names: string[] = [];

  for (const translation of tab.titleTranslations ?? []) {
    const rawValue = translation.value;

    if (typeof rawValue !== 'string') continue;

    const value = rawValue.trim();

    if (value.length > 0 && !names.includes(value)) {
      names.push(value);
    }
  }

  const title = typeof tab.title === 'string' ? tab.title.trim() : '';

  if (title && !names.includes(title)) {
    names.push(title);
  }

  return names;
};

export const getUsedTabs = (tabs: Tab[], sections: TemplateSection[]): Tab[] => {
  const usedTabIds = new Set<string>();

  for (const section of sections) {
    if (section.tabId) {
      usedTabIds.add(section.tabId);
    }
  }

  return tabs.filter((tab) => usedTabIds.has(tab.id));
};

export const isTabNameUnique = (tabs: Tab[], name: string, excludeTabId?: string): boolean => {
  const normalized = name.trim();

  if (!normalized) return true;

  return !tabs.some((tab) => {
    if (excludeTabId && tab.id === excludeTabId) return false;

    return getTabNameCandidates(tab).includes(normalized);
  });
};

export const hasUniqueTabNames = (tabs: Tab[], tab: Pick<Tab, 'title' | 'titleTranslations'>, excludeTabId?: string): boolean =>
  getTabNameCandidates(tab).every((name) => isTabNameUnique(tabs, name, excludeTabId));
